import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const userImageModules = import.meta.glob<{ default: string }>(
  '../assets/users/*.{jpg,jpeg,png}',
  { eager: true },
);
const userImages: string[] = Object.values(userImageModules).map((m) => m.default);

const AVATAR_COUNT_ROW1 = Math.ceil(userImages.length / 2);
const AVATAR_COUNT_ROW2 = Math.floor(userImages.length / 2);

const PhotoModal: React.FC<{ src: string; onClose: () => void }> = ({ src, onClose }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white/80 transition-colors hover:bg-white/30 hover:text-white"
          aria-label="Close"
        >
          ✕
        </button>
        <img
          src={src}
          alt="User photo"
          className="max-h-[85vh] max-w-[85vw] rounded-xl object-contain shadow-2xl"
        />
      </div>
    </div>
  );
};

function AvatarRow({
  images,
  startIndex,
  count,
  direction,
  onPhotoClick,
}: {
  images: string[];
  startIndex: number;
  count: number;
  direction: 'left' | 'right';
  onPhotoClick: (src: string) => void;
}) {
  const [paused, setPaused] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const avatars = Array.from({ length: count }, (_, i) => i);

  return (
    <div
      className={`overflow-hidden py-3 ${paused ? 'avatar-row-paused' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        setPaused(false);
        setHoveredIdx(null);
      }}
    >
      <div
        className={`flex gap-4 sm:gap-5 w-max ${
          direction === 'left' ? 'animate-scroll-left' : 'animate-scroll-right'
        }`}
      >
        {avatars.map((idx) => {
          const imageSrc = images[startIndex + idx];
          return (
            <AvatarItem
              key={`a-${idx}`}
              src={imageSrc}
              globalIdx={idx}
              hoveredIdx={hoveredIdx}
              onHover={setHoveredIdx}
              onClick={() => onPhotoClick(imageSrc)}
            />
          );
        })}
        {avatars.map((idx) => {
          const imageSrc = images[startIndex + idx];
          return (
            <AvatarItem
              key={`b-${idx}`}
              src={imageSrc}
              globalIdx={idx + count}
              hoveredIdx={hoveredIdx}
              onHover={setHoveredIdx}
              onClick={() => onPhotoClick(imageSrc)}
            />
          );
        })}
      </div>
    </div>
  );
}

function AvatarItem({
  src,
  globalIdx,
  hoveredIdx,
  onHover,
  onClick,
}: {
  src: string;
  globalIdx: number;
  hoveredIdx: number | null;
  onHover: (i: number | null) => void;
  onClick: () => void;
}) {
  const isHovered = hoveredIdx === globalIdx;

  return (
    <div
      className="shrink-0 cursor-pointer transition-transform duration-300 ease-out"
      style={{
        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
        zIndex: isHovered ? 10 : 1,
      }}
      onMouseEnter={() => onHover(globalIdx)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
    >
      <div
        className="h-16 w-16 overflow-hidden rounded-full transition-[border,box-shadow] duration-300 ease-out sm:h-20 sm:w-20 md:h-24 md:w-24"
        style={{
          border: isHovered ? '3px solid #6C5DD3' : '2px solid transparent',
          boxShadow: isHovered ? '0 0 20px rgba(108, 93, 211, 0.5)' : 'none',
        }}
      >
        <img src={src} alt="User" className="h-full w-full object-cover" />
      </div>
    </div>
  );
}

export const Community: React.FC = () => {
  const { t } = useTranslation();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const row1Images = userImages.slice(0, AVATAR_COUNT_ROW1);
  const row2Images = userImages.slice(AVATAR_COUNT_ROW1, AVATAR_COUNT_ROW1 + AVATAR_COUNT_ROW2);

  return (
    <section className="section-page overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="mx-auto mb-4 max-w-[min(100%,48rem)] text-balance text-2xl font-bold text-white md:text-3xl">
            {t('community-title')}
          </h2>
          <p className="mx-auto max-w-3xl text-pretty text-base leading-relaxed text-white/80">
            {t('community-desc')}
          </p>
        </div>

        <div className="space-y-2">
          <AvatarRow
            images={row1Images}
            startIndex={0}
            count={row1Images.length}
            direction="left"
            onPhotoClick={setLightboxSrc}
          />
          <AvatarRow
            images={row2Images}
            startIndex={0}
            count={row2Images.length}
            direction="right"
            onPhotoClick={setLightboxSrc}
          />
        </div>
      </div>

      {lightboxSrc && (
        <PhotoModal src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </section>
  );
};
